function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React from "react";
import { lifecycle, compose, branch } from "recompose";
import { withRouter, Link } from "react-router-dom";
import { MenuItem, MenuDivider, Tooltip, KeyCombo, ContextMenu, Menu, Classes, Icon } from "@blueprintjs/core";
import { startCase, omit, isNumber, flatMap, isArray, isString } from "lodash";
import fuzzysearch from "fuzzysearch";

var noop = function noop() {}; // https://github.com/palantir/blueprint/issues/2820


function MenuItemLink(_ref) {
  var text = _ref.text,
      onClick = _ref.onClick,
      icon = _ref.icon,
      navTo = _ref.navTo;

  var handleLinkClick = function handleLinkClick(e) {
    e.target.closest("." + Classes.POPOVER_DISMISS).click();
  };

  return /*#__PURE__*/React.createElement("li", {
    className: Classes.POPOVER_DISMISS,
    onClick: onClick
  }, /*#__PURE__*/React.createElement(Link, {
    onClick: handleLinkClick,
    to: navTo,
    className: "bp3-menu-item"
  }, icon && /*#__PURE__*/React.createElement(Icon, {
    icon: icon
  }), /*#__PURE__*/React.createElement("div", {
    className: "bp3-text-overflow-ellipsis bp3-fill"
  }, text)));
} // Enhanced MenuItem that supports history-based navigation when passed a
// `navTo` prop


export var EnhancedMenuItem = compose(lifecycle({
  componentDidMount: function componentDidMount() {
    var _this$props = this.props,
        _this$props$didMount = _this$props.didMount,
        didMount = _this$props$didMount === void 0 ? noop : _this$props$didMount,
        className = _this$props.className;
    didMount({
      className: className
    });
  },
  componentWillUnmount: function componentWillUnmount() {
    var _this$props2 = this.props,
        _this$props2$willUnmo = _this$props2.willUnmount,
        willUnmount = _this$props2$willUnmo === void 0 ? noop : _this$props2$willUnmo,
        className = _this$props2.className;
    willUnmount({
      className: className
    });
  }
}), branch(function (_ref2) {
  var navTo = _ref2.navTo;
  return navTo;
}, withRouter))(function (_ref3) {
  var navTo = _ref3.navTo,
      context = _ref3.context,
      staticContext = _ref3.staticContext,
      didMount = _ref3.didMount,
      willUnmount = _ref3.willUnmount,
      props = _objectWithoutPropertiesLoose(_ref3, ["navTo", "context", "staticContext", "didMount", "willUnmount"]);

  var MenuItemComp = MenuItem;

  if (navTo) {
    MenuItemComp = MenuItemLink;
  }

  return /*#__PURE__*/React.createElement(MenuItemComp, _extends({
    popoverProps: {
      autoFocus: false
    }
  }, navTo && {
    navTo: navTo
  }, props, {
    onClick: props.onClick ? function () {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      return props.onClick.apply(props, args.concat([context]));
    } : undefined
  }));
}); // First Non-Undefined

function fnu() {
  for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    args[_key2] = arguments[_key2];
  }

  return args.find(function (v) {
    return v !== undefined;
  });
} // Sets a tick icon if items has a `checked` prop


export var tickMenuEnhancer = function tickMenuEnhancer(def) {
  var out = _extends({}, def);

  if (out.checked !== undefined) {
    out.icon = out.checked ? "small-tick" : "blank";
  }

  return out;
}; // Derives various menu item props based on command objects matched via the `cmd`
// prop. Derived props include `text`, `icon`, `hotkey`, `onClick` and `disabled`.

export var commandMenuEnhancer = function commandMenuEnhancer(commands, config) {
  if (config === void 0) {
    config = {};
  }

  return function (def, context) {
    var cmdId = typeof def === "string" ? def : def.cmd;
    var item = typeof def === "string" ? {
      cmd: def
    } : _extends({}, def);
    var useTicks = fnu(item.useTicks, config.useTicks);
    delete item.useTicks;

    if (cmdId && commands[cmdId] && def.divider === undefined) {
      var command = commands[cmdId];
      var isActive = command.isActive,
          isDisabled = command.isDisabled,
          isHidden = command.isHidden;
      var toggles = isActive !== undefined;
      item.hidden = fnu(item.hidden, isHidden);
      item.disabled = fnu(item.disabled, isDisabled);
      item.key = item.key || cmdId;
      item.submenu = item.submenu || command.submenu;
      item.component = item.component || command.component;

      if (toggles) {
        if (useTicks) {
          item.text = item.text || command.shortName || command.name;
          item.checked = item.checked || isActive;
        } else {
          item.text = item.text || (isActive ? command.name : command.inactiveName || command.name);
          item.icon = item.icon || (isActive ? command.icon : command.inactiveIcon || command.icon);
        }
      } else {
        item.text = item.text || command.name;
        item.icon = item.icon || command.icon;
      }

      item.hotkey = item.hotkey || command.hotkey;

      if (!item.onClick) {
        item.onClick = function (event) {
          return command.execute({
            event: event,
            context: context,
            menuItem: item,
            viaMenu: true
          });
        };
      }
    } else if (cmdId && !commands[cmdId]) {
      item.text = item.text || startCase(cmdId);
      item.disabled = true;
    }

    if (config.omitIcons) {
      item.icon = undefined;
    }

    if (config.forceIconAlignment !== false) {
      item.icon = item.icon || "blank";
    }

    if (useTicks) {
      item = tickMenuEnhancer(item);
    }

    return item;
  };
};

var ident = function ident(x) {
  return x;
};

var dividerShorthandEnhancer = function dividerShorthandEnhancer(def) {
  return typeof def === "string" && def.startsWith("--") ? {
    divider: def.substr(2)
  } : def;
}; // filter out unwanted attributes here! we won't want these to show up on the dom element or react will give nasty warnings


var unwantedAttrs = ["isSimpleText", "justText", "submenu", "component", "hotkey", "changingProps", "showInSearchMenu", "hideFromMenuSearch"];
/** A menu item component that adds many features over the standard MenuItem,
 * and allows for dynamic menu structures that are computed efficiently (only
 * visible sections are computed and rendered).
 * TODO: document and add examples
 */

export var DynamicMenuItem = function DynamicMenuItem(_ref4) {
  var def = _ref4.def,
      _ref4$enhancers = _ref4.enhancers,
      enhancers = _ref4$enhancers === void 0 ? [ident] : _ref4$enhancers,
      context = _ref4.context,
      doNotEnhanceTopLevelItem = _ref4.doNotEnhanceTopLevelItem;
  // If passed an element instead of a menu item definition, return it.
  // This allows mixing menu item elements and menu item defs, and makes it
  // safe to call menu creation utils with their own output.
  if (React.isValidElement(def)) return def;
  var item = [dividerShorthandEnhancer].concat(doNotEnhanceTopLevelItem ? [ident] : enhancers).reduce(function (v, f) {
    return f(v, context);
  }, def);
  var out;

  if (item.divider !== undefined) {
    out = /*#__PURE__*/React.createElement(MenuDivider, item.divider ? {
      title: item.divider
    } : {});
  } else {
    var ItemComponent = item.component || EnhancedMenuItem;
    out = /*#__PURE__*/React.createElement(ItemComponent // filter out unwanted attributes here!
    , _extends({}, omit(item, unwantedAttrs), {
      context: context,
      icon: item.icon || item.iconName,
      labelElement: item.hotkey && /*#__PURE__*/React.createElement(KeyCombo, {
        minimal: true,
        combo: item.hotkey
      }),
      text: item.text
    }), item.submenu ? item.submenu.filter(ident).map(function (def, index) {
      return /*#__PURE__*/React.createElement(DynamicMenuItem, {
        def: def,
        enhancers: enhancers,
        context: context,
        key: index
      });
    }) : undefined);
  } // if (item.disabled && item.disabledTooltip) {
  //   item.tooltip = def.disabledTooltip
  // }


  if (item.disabled && typeof item.disabled === "string") {
    item.tooltip = item.disabled;
  }

  if (item.tooltip) {
    out = /*#__PURE__*/React.createElement(Tooltip, {
      content: item.tooltip
    }, out);
  }

  return item.hidden ? null : out;
}; // Map the passed item definition(s) to DynamicMenuItem elements

export var createDynamicMenu = function createDynamicMenu(menuDef, enhancers, context) {
  if (menuDef instanceof Array) {
    return menuDef.map(function (def, index) {
      return /*#__PURE__*/React.createElement(DynamicMenuItem, {
        def: def,
        enhancers: enhancers,
        context: context,
        key: index
      });
    });
  } else {
    return /*#__PURE__*/React.createElement(DynamicMenuItem, {
      def: menuDef,
      enhancers: enhancers,
      context: context
    });
  }
}; // Create a "bar" menu, keeping the top level array unchanged, and only
// map their submenus to DynamicMenuItem elements

export var createDynamicBarMenu = function createDynamicBarMenu(topMenuDef, enhancers, context) {
  return topMenuDef.map(function (topLevelItem) {
    var def = _extends({}, topLevelItem);

    if (def.submenu) {
      def.submenu = def.submenu.map(function (subdef, index) {
        return /*#__PURE__*/React.createElement(DynamicMenuItem, {
          def: subdef,
          enhancers: enhancers,
          context: context,
          key: index
        });
      });
    }

    return def;
  });
}; // Shorthand for command-based menus

export var createCommandMenu = function createCommandMenu(menuDef, commands, config, context) {
  return createDynamicMenu(menuDef, [commandMenuEnhancer(commands, config)], context);
}; // Shorthand for command-based bar menus

export var createCommandBarMenu = function createCommandBarMenu(menuDef, commands, config, context) {
  return createDynamicBarMenu(menuDef, [commandMenuEnhancer(commands, config)], context);
};
export function showCommandContextMenu(menuDef, commands, config, event, onClose, context) {
  return showContextMenu(menuDef, [commandMenuEnhancer(commands, config)], event, onClose, context);
}
/**
 * TODO: update documentation. This is now an alias of createDynamicMenu
 *
 * Creates the contents of a Blueprint menu based on a given menu structure.
 *
 * The input can be an array of item objects, where each may contain:
 * text: text to show
 * key: React key to use (optional)
 * divider: indicates it's a divider instead of an item. Use an empty string
 *   for a normal divider, or some label text for a labeled one
 * icon: name of icon to show (optional)
 * label: right-aligned label, used mostly for shortcuts (optional)
 * hotkey: right-aligned label formatted with <KeyCombo> (optional)
 * tooltip: tooltip text to use (optional)
 * submenu: nested menu structure describing submenu (i.e. array of item objects),
 *   or array of MenuItem elements
 * onClick: click handler
 * navTo: a url to navigate to (assumes react-router)
 * href: a url to link to
 * target: link target
 *
 * Since this function is recursive (to handle nested submenus), and React
 * elements passed as input are returned unchanged, it is possible to freely mix
 * item objects and MenuItem elements. That also makes it safe to call the function
 * with its own output.
 *
 * A customize function may also be provided, and allows customization or
 * replacement of the created MenuItems, allowing for custom props or behavior.
 * That function receives the original created element and the item object, and
 * must return an element.
 *
 * Usage example:
 *
 * const menu = createMenu([
 *   { text: 'Item One', icon: 'add', onClick: () => console.info('Clicked 1') },
 *   { text: 'Item One', onClick: () => console.info('Clicked 2') },
 *   { divider: '' },
 *   { text: 'Item Three', icon: 'numerical', onClick: () => console.info('Clicked 3') },
 *   { divider: '' },
 *   { text: 'Submenus', submenu: [
 *     { text: 'Sub One' },
 *     { text: 'Sub Two' },
 *   ]},
 * ]);
 *
 */

export var createMenu = createDynamicMenu;
export function showContextMenu(menuDef, enhancers, event, onClose, context, menuComp) {
  if (menuComp === void 0) {
    menuComp = Menu;
  }

  menuDef = filterMenuForCorrectness(menuDef);
  if (!menuDef) return;
  var MenuComponent = menuComp;
  event.persist && event.persist(); // Render a context menu at the passed event's position

  ContextMenu.show( /*#__PURE__*/React.createElement(MenuComponent, null, createDynamicMenu(menuDef, enhancers, context)), {
    left: event.clientX,
    top: event.clientY
  }, onClose);
  event.stopPropagation && event.stopPropagation();
  event.preventDefault && event.preventDefault();
}

function filterMenuForCorrectness(menuDef) {
  return menuDef && menuDef.length && menuDef.filter(ident);
}

export function getStringFromReactComponent(comp) {
  var _comp$props;

  if (!comp) return "";
  if (isString(comp) || isNumber(comp)) return comp;

  if ((_comp$props = comp.props) != null && _comp$props.text) {
    return getStringFromReactComponent(comp.props.text);
  }

  var _ref5 = comp.props || {},
      children = _ref5.children;

  if (!children) return "";
  if (isArray(children)) return flatMap(children, getStringFromReactComponent).join("");
  if (isString(children)) return children;

  if (children.props) {
    return getStringFromReactComponent(children.props);
  }
}
export function doesSearchValMatchText(searchVal, justText) {
  return fuzzysearch(searchVal ? searchVal.toLowerCase() : "", justText ? justText.toLowerCase() : "");
}
export var MenuItemWithTooltip = function MenuItemWithTooltip(_ref6) {
  var tooltip = _ref6.tooltip,
      rest = _objectWithoutPropertiesLoose(_ref6, ["tooltip"]);

  var out = /*#__PURE__*/React.createElement(MenuItem, rest);

  if (tooltip) {
    out = /*#__PURE__*/React.createElement(Tooltip, {
      content: tooltip
    }, out);
  }

  return out;
};